import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Dialog, DialogContent } from "./ui/dialog";
import { Button } from "./ui/button";
import { Clock, LogOut, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 60_000 * 3; // 3 min

/**
 * Globally mounted (in Layout). Polls /api/time-tracking/pending-prompt
 * every 3 min. If pending, shows a mandatory modal with 2 options:
 *   - Fazer Horas Extra  (accept)
 *   - Fim de Turno       (logout)
 * Modal cannot be dismissed with ESC / click-out.
 */
export default function OvertimePromptModal() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState(null);
  const [busy, setBusy] = useState(false);

  const poll = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/time-tracking/pending-prompt`);
      if (r.data?.pending) setPrompt(r.data);
    } catch (_) { /* ignore */ }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    // Poll imediato + intervalo
    poll();
    const t = setInterval(poll, POLL_INTERVAL_MS);
    // Deep-link: se URL contém ?overtime_prompt=1 (via push notification click)
    if (new URLSearchParams(window.location.search).get("overtime_prompt")) {
      poll();
    }
    return () => clearInterval(t);
  }, [user, poll]);

  const respond = async (action) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/time-tracking/overtime-response`, { action });
      toast.success(r.data.message);
      setPrompt(null);
      if (action === "logout") {
        // Fazer logout completo
        setTimeout(() => {
          logout?.();
          navigate("/login", { replace: true });
        }, 800);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally {
      setBusy(false);
    }
  };

  if (!prompt) return null;

  return (
    <Dialog open onOpenChange={() => { /* bloqueado — só fecha ao responder */ }}>
      <DialogContent
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="max-w-md"
        data-testid="overtime-prompt-modal"
      >
        <div className="text-center py-4">
          <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4 animate-pulse">
            <Clock className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Fim de Turno?</h2>
          <p className="text-slate-600 mt-2">
            Estás aqui há <strong className="text-slate-900">{prompt.hours_elapsed}h</strong>.
            <br />
            Queres continuar (horas extra) ou fazer <em>logout</em>?
          </p>
          {prompt.overtime_count > 0 && (
            <p className="text-xs text-amber-700 mt-2 font-semibold">
              Já aceitaste continuar {prompt.overtime_count}× hoje.
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
          <Button
            variant="outline"
            className="h-16 border-red-300 text-red-700 hover:bg-red-50 flex flex-col gap-1"
            disabled={busy}
            onClick={() => respond("logout")}
            data-testid="btn-shift-logout"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-bold">Fim de Turno</span>
            <span className="text-xs font-normal opacity-80">Logout automático</span>
          </Button>
          <Button
            className="h-16 bg-emerald-600 hover:bg-emerald-700 text-white flex flex-col gap-1"
            disabled={busy}
            onClick={() => respond("accept")}
            data-testid="btn-shift-overtime"
          >
            <TrendingUp className="h-5 w-5" />
            <span className="font-bold">Horas Extra</span>
            <span className="text-xs font-normal opacity-90">Volto a perguntar em 4h</span>
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
